import type { GoonSessionSummary } from "@gooner/shared";
import { fmtCount, fmtDurationMs, fmtGapMs, humanBytes } from "../lib/statFormat";
import {
  BRAND_NAME_URL,
  BRAND_DOTCOM_URL,
  GOLD,
  GOLD_SOFT,
  LABEL,
  MUTED,
  SITE_HOST,
  TEXT,
  TILE_FILL,
  TILE_STROKE,
  TRACK,
  drawBrandLockup,
  drawCardGround,
  drawCoverImage,
  drawImageH,
  drawQr,
  drawStatTile,
  fitText,
  goldA,
  loadBrandImages,
  loadPosters,
  roundRect,
} from "../control/ui/cardKit";

/**
 * The post-session share card.
 *
 * 1080×1350, the same 4:5 and the same chrome as the profile card (ui/cardKit) — the two
 * are a set, and the session card being a lone 1080×1350 square in a different palette is
 * what this replaces. What it says is entirely its own: one sitting, charted.
 *
 * THE CLIMB is the centrepiece and the reason `first_viewed_at` exists: a staircase of how
 * many DISTINCT clips had gone by at each moment, with a dot on the curve wherever an edge
 * landed. Together they read as the shape of the session — a flat stretch is a clip on
 * repeat, a steep one is hunting, and the dots say which of the two got you there.
 */

const CARD_W = 1080;
const CARD_H = 1350; // 4:5 — the social-feed sweet spot, same as the profile card
const M = 72;
/** Where the card's QR lands. The front door, not a profile: a session can be anonymous,
 *  and a scanner sent to /u/undefined is worse than one sent to the site. */
const CARD_URL = `https://${SITE_HOST}`;

/** "FRI 3:47 AM" — the session's start, in the viewer's own timezone. */
function fmtWhen(at: number): string {
  const d = new Date(at);
  const day = d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
  const time = d
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .toUpperCase();
  return `${day} ${time}`;
}

/** The gold ramp the taste bar steps through, richest slice first. */
const TASTE_ALPHAS = [1, 0.78, 0.6, 0.44, 0.32, 0.24];

/**
 * The climb: a cumulative-unique-clips staircase with the edges marked on it.
 *
 * A STEP function, not a line through the points, because that is what the data is —
 * the count is flat between sightings and jumps by one when a new clip appears. Drawing a
 * smooth line between them would invent clips at times nothing played.
 */
function drawClimb(
  ctx: CanvasRenderingContext2D,
  s: GoonSessionSummary,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const start = s.session.startedAt;
  const end = s.session.endedAt ?? Date.now();
  const span = Math.max(1, end - start);
  const maxUnique = Math.max(1, s.uniqueClips);
  // The floor of the plot is not the floor of the box. The bottom AXIS strip carries the
  // time labels and the edge rug, and the curve's first point sits exactly on zero — draw
  // them in the same band and the curve runs straight through "0:00".
  const AXIS_H = 38;
  const floor = y + h - AXIS_H;
  const px = (t: number) => x + (Math.min(Math.max(t, start), end) - start) / span * w;
  const py = (n: number) => floor - (n / maxUnique) * (floor - y - 14);

  // Plot ground + two faint guides, so the curve has something to be read against.
  roundRect(ctx, x, y, w, h, 14);
  ctx.fillStyle = TILE_FILL;
  ctx.fill();
  ctx.strokeStyle = TILE_STROKE;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.save();
  ctx.strokeStyle = TRACK;
  ctx.lineWidth = 1;
  for (const frac of [0.5, 1]) {
    ctx.beginPath();
    ctx.moveTo(x + 1, py(maxUnique * frac));
    ctx.lineTo(x + w - 1, py(maxUnique * frac));
    ctx.stroke();
  }
  // The axis rule, separating the plot from the strip the labels live in.
  ctx.beginPath();
  ctx.moveTo(x + 1, floor);
  ctx.lineTo(x + w - 1, floor);
  ctx.stroke();
  ctx.restore();

  if (!s.climb.length) {
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = LABEL;
    ctx.font = "500 24px system-ui, sans-serif";
    ctx.fillText("nothing played this session", x + w / 2, y + h / 2);
    ctx.textBaseline = "alphabetic";
    return;
  }

  // The staircase, as one path reused for both the fill and the stroke.
  const steps = () => {
    ctx.beginPath();
    ctx.moveTo(px(s.climb[0].at), py(0));
    let lastY = py(0);
    for (const p of s.climb) {
      const cx = px(p.at);
      ctx.lineTo(cx, lastY); // hold the level until the new clip appears
      lastY = py(p.unique);
      ctx.lineTo(cx, lastY); // then step up by one
    }
    ctx.lineTo(px(end), lastY); // and run flat to the end of the session
  };

  // ONE clip for the fill, the stroke AND the dots. The stroke is 3px wide and the curve
  // runs along the very edges of the box — its first point sits on the floor and its last
  // on the right wall — so an unclipped stroke bleeds past the rounded corners and the
  // panel reads as a broken rectangle. It looked like an overflowing chart; it was half a
  // line width.
  ctx.save();
  roundRect(ctx, x, y, w, h, 14);
  ctx.clip();

  steps();
  ctx.lineTo(px(end), py(0));
  ctx.lineTo(px(s.climb[0].at), py(0));
  ctx.closePath();
  const fill = ctx.createLinearGradient(0, y, 0, y + h);
  fill.addColorStop(0, goldA(0.4));
  fill.addColorStop(1, goldA(0.04));
  ctx.fillStyle = fill;
  ctx.fill();

  steps();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.stroke();

  // ---- the edges, ON the curve ----
  // Where the curve was when each edge landed: the number of clips first seen at or
  // before that moment. A binary search would be tidier; with edges in the tens and
  // clips in the hundreds, a scan is not worth the extra code to get wrong.
  for (const t of s.session.edgeTimes) {
    if (t < start || t > end) continue;
    const n = s.climb.filter((p) => p.at <= t).length;
    const cx = px(t);
    const cy = py(n);
    // An ink collar first, so two edges a minute apart stay two dots rather than a blob.
    ctx.beginPath();
    ctx.arc(cx, cy, 7.5, 0, Math.PI * 2);
    ctx.fillStyle = "#0a0a0b";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, 5.5, 0, Math.PI * 2);
    ctx.fillStyle = GOLD_SOFT;
    ctx.fill();
    // A tick on the floor too, so a flurry that overlaps on the curve still reads as
    // several presses rather than one fat dot.
    // Tight under the axis rule, ABOVE the time labels' cap height — the rug and the
    // labels share this strip, and at the right-hand end the last tick and "2h 14m" land
    // on the same pixels unless the two are given separate bands.
    ctx.fillStyle = goldA(0.55);
    ctx.fillRect(cx - 1, floor + 3, 2, 8);
  }
  ctx.restore();

  // ---- scale ----
  ctx.textAlign = "left";
  ctx.fillStyle = MUTED;
  ctx.font = "600 20px system-ui, sans-serif";
  ctx.fillText(`${fmtCount(s.uniqueClips)} unique`, x + 16, y + 30);
  ctx.fillStyle = LABEL;
  ctx.font = "500 19px system-ui, sans-serif";
  ctx.fillText("0:00", x + 16, y + h - 10);
  ctx.textAlign = "right";
  ctx.fillText(fmtDurationMs(span), x + w - 16, y + h - 10);
}

/** The taste bar: one 100%-stacked row over the session's facet mix, plus a legend. */
function drawTaste(
  ctx: CanvasRenderingContext2D,
  s: GoonSessionSummary,
  x: number,
  y: number,
  w: number,
): number {
  const slices = s.facets.slice(0, 4);
  if (!slices.length) return y;
  ctx.textAlign = "left";
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = "700 23px system-ui, sans-serif";
  ctx.fillText("TONIGHT'S TYPE", x, y);

  // Normalised across the slices SHOWN, so the bar always fills its width. The honest
  // per-facet share is in the legend's percentages, which come straight from the server.
  const total = slices.reduce((a, b) => a + b.clips, 0) || 1;
  const barY = y + 16;
  const barH = 26;
  ctx.save();
  roundRect(ctx, x, barY, w, barH, barH / 2);
  ctx.clip();
  let cx = x;
  slices.forEach((sl, i) => {
    const sw = (sl.clips / total) * w;
    ctx.fillStyle = goldA(TASTE_ALPHAS[i] ?? 0.2);
    ctx.fillRect(cx, barY, sw, barH);
    cx += sw;
  });
  ctx.restore();

  // Legend: swatch, label, share.
  const legendY = barY + barH + 32;
  let lx = x;
  ctx.font = "600 21px system-ui, sans-serif";
  slices.forEach((sl, i) => {
    const text = `${sl.label} ${Math.round(sl.share * 100)}%`;
    const tw = ctx.measureText(text).width;
    if (lx + 20 + tw > x + w) return; // silently drop what will not fit rather than overrun
    ctx.fillStyle = goldA(TASTE_ALPHAS[i] ?? 0.2);
    roundRect(ctx, lx, legendY - 13, 13, 13, 3);
    ctx.fill();
    ctx.fillStyle = MUTED;
    ctx.fillText(text, lx + 20, legendY);
    lx += 20 + tw + 26;
  });
  return legendY;
}

/**
 * Render the card. Async because the highlight posters must be decoded before anything is
 * drawn — see `loadPosters` for the safe-mode / tainting / timeout rules it enforces.
 */
export async function renderSessionCard(
  s: GoonSessionSummary,
  username?: string,
): Promise<HTMLCanvasElement> {
  const shots: Array<{ label: string; hl: typeof s.mostSeen; caption: string }> = [
    {
      label: "Most watched",
      hl: s.mostSeen,
      caption: !s.mostSeen
        ? "—"
        : s.mostSeen.seconds
          ? `${fmtDurationMs(s.mostSeen.seconds * 1000)} watched`
          : `${fmtCount(s.mostSeen.views)}× played`,
    },
    {
      label: "Most edged",
      hl: s.mostEdged,
      caption: s.mostEdged ? `${fmtCount(s.mostEdged.edges)} edge${s.mostEdged.edges === 1 ? "" : "s"}` : "—",
    },
    {
      label: "Most fapped",
      hl: s.mostFapped,
      caption: s.mostFapped ? `${fmtCount(s.mostFapped.taps ?? 0)} taps` : s.signedIn ? "—" : "sign in",
    },
  ];
  const [images, brand] = await Promise.all([
    loadPosters(
      shots
        .map((sh) => sh.hl)
        .filter((h): h is NonNullable<typeof h> => !!h)
        .map((h) => ({ id: h.clip.id, posterUrl: h.clip.posterUrl })),
    ),
    loadBrandImages([BRAND_NAME_URL, BRAND_DOTCOM_URL]),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  ctx.textBaseline = "alphabetic";
  const durationMs = (s.session.endedAt ?? Date.now()) - s.session.startedAt;

  drawCardGround(ctx, CARD_W, CARD_H);

  // ---- header: whose session (left, big) · the logo (right) ----
  //
  // The NAME is the headline. It is the one thing on the card that is about the person
  // rather than the numbers, and a card someone posts should lead with them. That puts the
  // logo in the corner, which is where a logo belongs on someone else's story anyway.
  //
  // The lockup here is the no-TLD one; the footer carries the ".com". Top says who, bottom
  // says where — putting the url in both spends the header on a repeat. Falls back to the
  // drawn lockup if the artwork never arrived, so the corner is never empty.
  //
  // NO TIER PILL. Every other block is a measurement; a "CERTIFIED GOON LORD" badge was
  // the one thing on the page grading you. The tier still exists — the recap lists it
  // beside the score — it just does not head the card.
  const nameImg = brand.get(BRAND_NAME_URL);
  const logoH = 44;
  let logoW = 0;
  if (nameImg) {
    logoW = (nameImg.naturalWidth / Math.max(1, nameImg.naturalHeight)) * logoH;
    drawImageH(ctx, nameImg, CARD_W - M - logoW, 74, logoH);
  } else {
    ctx.save();
    ctx.textAlign = "left";
    logoW = drawBrandLockup(ctx, CARD_W - M - 210, 112, 32);
    ctx.restore();
  }

  ctx.textAlign = "left";
  ctx.fillStyle = TEXT;
  const headline = username ? `@${username}` : "GOON SESSION";
  // Fitted, because a handle is user input: 20 characters must shrink rather than run
  // under the logo.
  fitText(ctx, headline, CARD_W - 2 * M - logoW - 36, 58, 30);
  ctx.fillText(headline, M, 118);

  // ---- eyebrow ----
  ctx.fillStyle = MUTED;
  ctx.font = "600 25px system-ui, sans-serif";
  ctx.fillText(
    (username ? ["GOON SESSION", fmtWhen(s.session.startedAt)] : [fmtWhen(s.session.startedAt)])
      .join("  ·  "),
    M,
    166,
  );

  // ---- hero: intensity ring + the comparison hook ----
  const gcx = M + 94;
  const gcy = 292;
  const gr = 74;
  ctx.lineWidth = 16;
  ctx.lineCap = "round";
  ctx.strokeStyle = TRACK;
  ctx.beginPath();
  ctx.arc(gcx, gcy, gr, 0, Math.PI * 2);
  ctx.stroke();
  const score = Math.max(0, Math.min(100, s.score));
  if (score > 0) {
    ctx.strokeStyle = GOLD;
    ctx.beginPath();
    ctx.arc(gcx, gcy, gr, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * score) / 100);
    ctx.stroke();
  }
  ctx.textAlign = "center";
  ctx.fillStyle = TEXT;
  ctx.font = "bold 54px system-ui, sans-serif";
  ctx.fillText(String(score), gcx, gcy + 6);
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = "700 18px system-ui, sans-serif";
  ctx.fillText("INTENSITY", gcx, gcy + 36);

  const hx = gcx + gr + 46;
  const hw = CARD_W - M - hx;
  ctx.textAlign = "left";
  if (s.ranked) {
    // The hook. Only shown once there are enough sessions on record for a percentile to
    // mean anything — see SESSION_RANK_FLOOR.
    ctx.fillStyle = MUTED;
    ctx.font = "600 26px system-ui, sans-serif";
    ctx.fillText("OUT-GOONED", hx, 240);
    ctx.fillStyle = GOLD;
    ctx.font = "900 96px system-ui, sans-serif";
    ctx.fillText(`${s.duration.pct}%`, hx, 328);
    ctx.fillStyle = MUTED;
    ctx.font = "500 24px system-ui, sans-serif";
    // Spelled out, not fmtCount'd: "#12 of 3k" reads as an estimate, and the whole point
    // of a rank is that it is exact.
    ctx.fillText(
      `of all goon sessions · #${s.duration.rank.toLocaleString()} of ${s.totalSessions.toLocaleString()}`,
      hx,
      366,
    );
  } else {
    // Under the floor there is no honest comparison to STRANGERS, so the hook compares you
    // to yourself instead — a real number either way. It deliberately does not repeat the
    // tier: that is already the pill in the corner, and a card that says "Certified Goon
    // Lord" twice in one glance has spent its biggest slot saying nothing new.
    const ownable = s.ownTotal > 1;
    ctx.fillStyle = MUTED;
    ctx.font = "600 26px system-ui, sans-serif";
    ctx.fillText(ownable ? "YOUR OWN RANKING" : "TONIGHT", hx, 240);
    ctx.fillStyle = GOLD;
    const hero = ownable ? `#${s.ownRank}` : fmtDurationMs(durationMs);
    fitText(ctx, hero, hw, 96, 46);
    ctx.fillText(hero, hx, 328);
    ctx.fillStyle = MUTED;
    ctx.font = "500 24px system-ui, sans-serif";
    ctx.fillText(
      ownable
        ? `longest of your ${fmtCount(s.ownTotal)} sessions`
        : "your first session on record",
      hx,
      366,
    );
  }

  // ---- headline stat tiles ----
  const tiles: Array<[string, string, boolean]> = [
    ["Gooned", fmtDurationMs(durationMs), false],
    ["Played", fmtCount(s.session.views), false],
    ["Unique", fmtCount(s.uniqueClips), false],
    ["Edges", fmtCount(s.session.edges), true],
    ["Burned", humanBytes(s.session.bytes), false],
  ];
  const tY = 400;
  const tH = 116;
  const gap = 16;
  const tW = (CARD_W - 2 * M - (tiles.length - 1) * gap) / tiles.length;
  tiles.forEach(([label, value, hot], i) => {
    drawStatTile(ctx, M + i * (tW + gap), tY, tW, tH, value, label, hot);
  });

  // ---- one-line insights ----
  // Only what actually happened. An absent edge leaves the bit out rather than printing a
  // zero, which would read as a measurement rather than an absence.
  const bits: string[] = [];
  if (s.msToFirstEdge != null) bits.push(`first edge at ${fmtGapMs(s.msToFirstEdge)}`);
  if (s.longestEdgeGapMs != null) bits.push(`${fmtGapMs(s.longestEdgeGapMs)} longest dry spell`);
  if (s.peakWindowEdges > 1) bits.push(`${s.peakWindowEdges} edges in 5 min at peak`);
  if (!bits.length && s.estimatedWatchSec > 0) {
    bits.push(`${fmtDurationMs(s.estimatedWatchSec * 1000)} of video on screen`);
  }
  if (s.personalBests.length) bits.push("personal best");
  ctx.textAlign = "left";
  ctx.fillStyle = MUTED;
  ctx.font = "500 24px system-ui, sans-serif";
  ctx.fillText(bits.slice(0, 3).join("   ·   "), M, 558);

  // ---- THE CLIMB ----
  let y = 598;
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = "700 23px system-ui, sans-serif";
  ctx.fillText("THE CLIMB", M, y);
  ctx.fillStyle = LABEL;
  ctx.font = "500 20px system-ui, sans-serif";
  ctx.fillText(
    s.approximateClimb
      ? "unique clips over time · edges marked · approximate"
      : "unique clips over time · edges marked",
    M + 150,
    y,
  );
  drawClimb(ctx, s, M, y + 14, CARD_W - 2 * M, 180);
  y += 14 + 180 + 46;

  // ---- ON REPEAT ----
  ctx.textAlign = "left";
  ctx.fillStyle = GOLD_SOFT;
  ctx.font = "700 23px system-ui, sans-serif";
  ctx.fillText("ON REPEAT", M, y);
  y += 16;
  const sGap = 16;
  const sW = (CARD_W - 2 * M - 2 * sGap) / 3;
  const sH = 130;
  shots.forEach((sh, i) => {
    const x = M + i * (sW + sGap);
    const img = sh.hl ? images.get(sh.hl.clip.id) : undefined;
    if (img) drawCoverImage(ctx, img, x, y, sW, sH, 18);
    else {
      ctx.save();
      roundRect(ctx, x, y, sW, sH, 18);
      ctx.clip();
      ctx.fillStyle = TILE_FILL;
      ctx.fillRect(x, y, sW, sH);
      ctx.restore();
    }
    roundRect(ctx, x, y, sW, sH, 18);
    ctx.strokeStyle = TILE_STROKE;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.textAlign = "left";
    ctx.fillStyle = img ? TEXT : LABEL;
    ctx.font = "600 20px system-ui, sans-serif";
    ctx.fillText(sh.label.toUpperCase(), x + 16, y + 30);
    ctx.fillStyle = sh.hl ? (img ? TEXT : "#d4d4d8") : "#3f3f46";
    ctx.font = `${sh.hl ? "bold 25px" : "500 21px"} system-ui, sans-serif`;
    ctx.fillText(sh.caption, x + 16, y + sH - 18);
  });
  y += sH + 44;

  // ---- TONIGHT'S TYPE ----
  drawTaste(ctx, s, M, y, CARD_W - 2 * M);

  // ---- footer: QR + hook, pinned to the bottom ----
  const qs = 140;
  const qy = CARD_H - 60 - qs;
  drawQr(ctx, CARD_URL, M, qy, qs);
  const fx = M + qs + 36;
  ctx.textAlign = "left";
  ctx.fillStyle = MUTED;
  ctx.font = "600 25px system-ui, sans-serif";
  ctx.fillText("Scan to track your Goon Sessions and more at:", fx, qy + 42);
  // The url, set as the ARTWORK rather than typed out in a system font — this line is the
  // whole point of the card, and it should look like the brand rather than like text about
  // the brand. Note the asset already ENDS IN ".com": despite the filename it is the full
  // url lockup, not a bare wordmark, so nothing is appended. Text fallback keeps the url
  // readable if the png never arrives.
  const wordImg = brand.get(BRAND_DOTCOM_URL);
  if (wordImg) drawImageH(ctx, wordImg, fx, qy + 54, 64);
  else {
    ctx.fillStyle = GOLD;
    ctx.font = "900 52px system-ui, sans-serif";
    ctx.fillText(SITE_HOST, fx, qy + 104);
  }
  ctx.fillStyle = LABEL;
  ctx.font = "500 23px system-ui, sans-serif";
  ctx.fillText("Free to browse · no signup to look", fx, qy + 138);

  return canvas;
}
